export const avatarMixin = {
  computed: {
    apiBaseUrl() {
      return process.env.VUE_APP_API_URL?.replace('/api/v1', '') || 'http://127.0.0.1:8000';
    }
  },

  methods: {
    getAvatarUrl(avatar, fallbackName = 'User') {
      if (!avatar) {
        return this.generateFallbackAvatar(fallbackName);
      }

      if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
        return avatar;
      }

      if (avatar.startsWith('/storage/')) {
        return `${this.apiBaseUrl}${avatar}`;
      }

      if (typeof avatar === 'string' && avatar.includes('avatars/')) {
        return `${this.apiBaseUrl}/storage/${avatar}`;
      }

      return avatar;
    },

    getUserAvatar(user) {

      const currentUser = user || this.user;

      if (currentUser?.avatar) {
        return this.getAvatarUrl(currentUser.avatar, currentUser.name);
      }

      if (currentUser?.id) {
        // Try PNG first (since that's your current format), then JPG
        const formats = ['png', 'jpg', 'jpeg', 'webp'];
        const format = formats[0]; // Start with PNG

        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.${format}`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return fullUrl;
      }

      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Alternative method that tries multiple formats synchronously
    getUserAvatarWithFormats(user, preferredFormat = 'jpg') {
      const currentUser = user || this.user;

      // Check for avatar in user object first
      if (currentUser?.avatar) {
        return this.getAvatarUrl(currentUser.avatar, currentUser.name);
      }

      // Try the expected path with preferred format
      if (currentUser?.id) {
        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.${preferredFormat}`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return fullUrl;
      }

      // Fallback
      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Method to handle avatar loading errors (use in templates with @error)
    onAvatarError(event, user) {
      console.warn('Avatar failed to load:', event.target.src);

      const currentUser = user || this.user;

      if (currentUser?.id) {
        const currentSrc = event.target.src;
        const formats = ['png', 'jpg', 'jpeg', 'webp'];

        // Find which format failed and try the next one
        let currentFormatIndex = -1;
        for (let i = 0; i < formats.length; i++) {
          if (currentSrc.includes(`avatar.${formats[i]}`)) {
            currentFormatIndex = i;
            break;
          }
        }

        // Try next format
        if (currentFormatIndex >= 0 && currentFormatIndex < formats.length - 1) {
          const nextFormat = formats[currentFormatIndex + 1];
          const nextPath = `${this.apiBaseUrl}/storage/avatars/${currentUser.id}/avatar.${nextFormat}`;
          console.log(`Trying next format (${nextFormat}):`, nextPath);
          event.target.src = nextPath;
          return;
        }
      }

      // All formats failed or no user ID, use fallback avatar
      const userName = currentUser?.name || 'User';
      event.target.src = this.generateFallbackAvatar(userName);
    },

    // Async version for cases where you need to test existence
    async testAvatarPath(avatarPath, userName) {
      const fullUrl = `${this.apiBaseUrl}${avatarPath}`;

      try {
        // Test if the avatar exists
        const exists = await this.checkImageExists(fullUrl);
        if (exists) {
          return fullUrl;
        } else {
          return this.generateFallbackAvatar(userName);
        }
      } catch (error) {
        return this.generateFallbackAvatar(userName);
      }
    },

    checkImageExists(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;

        // Timeout after 2 seconds
        setTimeout(() => resolve(false), 2000);
      });
    },

    generateFallbackAvatar(name) {
      // Generate initials from name
      const initials = this.getInitials(name);

      // Use UI-Avatars as primary fallback
      const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=009CAD&color=ffffff&size=120&bold=true`;

      return fallbackUrl;
    },

    getInitials(name) {
      if (!name) return 'U';

      return name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    },

    // Enhanced version that tries multiple formats in sequence
    getUserAvatarWithFallback(user) {
      const currentUser = user || this.user;

      // Check for avatar in user object first
      if (currentUser?.avatar) {
        return this.getAvatarUrl(currentUser.avatar, currentUser.name);
      }

      // Try expected path with jpg (most common)
      if (currentUser?.id) {
        const expectedPath = `/storage/avatars/${currentUser.id}/avatar.jpg`;
        const fullUrl = `${this.apiBaseUrl}${expectedPath}`;
        return fullUrl;
      }

      // Fallback to generated avatar
      const userName = currentUser?.name || 'User';
      return this.generateFallbackAvatar(userName);
    },

    // Synchronous version for immediate use (kept for backward compatibility)
    getUserAvatarSync(user) {
      return this.getUserAvatar(user); // Now uses the improved synchronous logic
    },

    // Method to get avatar with error handling for templates
    getAvatarWithErrorHandling(user, event = null) {
      if (event) {
        // This is called from an error handler
        const currentUser = user || this.user;
        const userName = currentUser?.name || 'User';
        return this.generateFallbackAvatar(userName);
      } else {
        // Normal avatar getting
        return this.getUserAvatar(user);
      }
    }
  }
};
